import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IShippingSearchDownload, ShippingReport, ShippingSearch, ShippingSearchDownload } from '@core/models';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom } from 'rxjs';
import {
  loadShippingSearch,
  loadShippingSearchDownloads,
  saveShippingReport,
  saveShippingSearch,
  setShippingSearchDownloadsSortOrder,
  unloadShippingReport
} from '../store/actions/order.actions';
import { AppState } from '../store/reducers/index';
import { ApicService } from './apic.services';
import { ConfigService } from './config.service';

@Injectable()
export class OrdersService {

  baseOrderPath: string;
  payerPortalPath: string;

  constructor(private configService: ConfigService, private httpClient: HttpClient, private store: Store<AppState>, private apicService: ApicService) {
    this.baseOrderPath = `${this.apicService.apiPath}${this.configService.appConfig.api.baseOrderUrl}`;
    this.payerPortalPath = `${this.apicService.apiPath}${this.configService.appConfig.api.baseUrl}`;
  }

  shippingSearchDownloadFactory(downloadData: IShippingSearchDownload): ShippingSearchDownload {
    return new ShippingSearchDownload(downloadData);
  }

  shippingSearch(shippingSearchData: any): Promise<any> {
    return firstValueFrom(this.httpClient.post(`${this.baseOrderPath}/shipping`, shippingSearchData, { observe: 'response' }))
      .then(response => response)
      .catch(response => response);
  }

  shippingSearchDownload(shippingSearchData: any): Promise<any> {
    return firstValueFrom(this.httpClient.post(`${this.baseOrderPath}/shipping/downloads`, shippingSearchData, { observe: 'response' }))
      .then(response => response)
      .catch(response => response);
  }

  getShippingSearchDownloads(): Promise<ShippingSearchDownload[]> {
    return firstValueFrom(this.httpClient
      .get<IShippingSearchDownload[]>(`${this.baseOrderPath}/shipping/downloads`))
      .then(response => {
        const shippingSearchDownloads: ShippingSearchDownload[] = [];

        if (response && response.length > 0) {
          response.forEach(downloadData => {
            shippingSearchDownloads.push(this.shippingSearchDownloadFactory(downloadData));
          });
        }

        return shippingSearchDownloads;
      })
      .catch(() => []);
  }

  downloadShippingSearchDownload(downloadId: number): Promise<any> {
    return firstValueFrom(this.httpClient.get(`${this.baseOrderPath}/shipping/downloads/${downloadId}`, { responseType: 'blob' }));
  }

  deleteShippingSearchDownload(downloadId: number): Observable<any> {
    return this.httpClient.delete(`${this.baseOrderPath}/shipping/downloads/${downloadId}`);
  }

  getUserTrustedToken(controlId: string): Promise<any> {
    return firstValueFrom(this.httpClient.get(`${this.payerPortalPath}/tableau/analyticReports/token/${controlId}`, { observe: 'response' }))
      .then(response => response)
      .catch(response => response);
  }

  loadShippingSearch(): void {
    this.store.dispatch(loadShippingSearch());
  }

  saveShippingSearch(shippingSearch: ShippingSearch): void {
    this.store.dispatch(saveShippingSearch({ payload: shippingSearch }));
  }

  unloadShippingReport(): void {
    this.store.dispatch(unloadShippingReport());
  }

  saveShippingReport(shippingReport: ShippingReport): void {
    this.store.dispatch(saveShippingReport({ payload: shippingReport }));
  }

  loadShippingSearchDownloads(): void {
    this.store.dispatch(loadShippingSearchDownloads());
  }

  setShippingSearchDownloadsSortOrder(key: string): void {
    this.store.dispatch(setShippingSearchDownloadsSortOrder({ payload: key }));
  }

}
